import React, { useEffect, useState, useMemo } from "react";
import {
  FaTimes,
  FaShieldAlt,
  FaSearch,
  FaCheck,
  FaLock,
  FaUnlock,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { domain } from "../../../security";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";

const MenuStructure = [
  {
    category: "General",
    permissions: [
      { title: "Dashboard", permissionKey: "dashboard" },
      { title: "Help & FAQ", permissionKey: "helpFaq" },
    ],
  },
  {
    category: "Product Setup",
    permissions: [
      { title: "Product Setup Access", permissionKey: "productSetup" },
      { title: "Inventory Status", permissionKey: "inventoryStatus" },
      { title: "Unit Of Measurement", permissionKey: "unitOfMeasurement" },
      { title: "Item Types", permissionKey: "itemTypes" },
      { title: "Categories", permissionKey: "categories" },
      { title: "Categories 2", permissionKey: "categories2" },
      { title: "Categories 3", permissionKey: "categories3" },
      { title: "Categories 4", permissionKey: "categories4" },
      { title: "Categories 5", permissionKey: "categories5" },
      { title: "Brands", permissionKey: "brands" },
      { title: "Pricelists", permissionKey: "pricelists" },
      { title: "Warehouses", permissionKey: "warehouses" },
      { title: "Production Tagging", permissionKey: "productionTagging" },
      { title: "Logistics Personnel", permissionKey: "logisticsPersonnel" },
      { title: "Vehicles", permissionKey: "vehicles" },
      { title: "Terms", permissionKey: "terms" },
    ],
  },
  {
    category: "Procurement",
    permissions: [
      { title: "Procurement Access", permissionKey: "procurement" },
      { title: "Purchase Orders", permissionKey: "purchaseOrders" },
      { title: "Goods Receipts", permissionKey: "goodsReceipts" },
    ],
  },
  {
    category: "Sales",
    permissions: [
      { title: "Sales Access", permissionKey: "sales" },
      { title: "Sales Quotations", permissionKey: "salesQuotations" },
      { title: "All Quotation Sales", permissionKey: "allQuotationSales" }, // New
      { title: "Sales Orders", permissionKey: "salesOrders" },
      { title: "Delivery Orders", permissionKey: "deliveryOrders" },
      // { title: "Production", permissionKey: "production" },
      // { title: "Operations Production", permissionKey: "operationsProduction" },
      // { title: "Warehouse Requests", permissionKey: "warehouseRequests" },
      // { title: "Warehouse Stock", permissionKey: "warehouseStock" },
    ],
  },
  {
    category: "Production",
    permissions: [
      // { title: "Sales Access", permissionKey: "sales" },
      // { title: "Sales Quotations", permissionKey: "salesQuotations" },
      // { title: "All Quotation Sales", permissionKey: "allQuotationSales" }, // New
      // { title: "Sales Orders", permissionKey: "salesOrders" },
      // { title: "Delivery Orders", permissionKey: "deliveryOrders" },
      { title: "Production", permissionKey: "production" },
      { title: "Operations Production", permissionKey: "operationsProduction" },
      { title: "Warehouse Requests", permissionKey: "warehouseRequests" },
      { title: "Warehouse Stock", permissionKey: "warehouseStock" },
    ],
  },
  {
    category: "Shipment & Delivery",
    permissions: [{ title: "Shipment", permissionKey: "shipment" }],
  },
  {
    category: "Returns",
    permissions: [
      { title: "Returns Access", permissionKey: "returns" },
      { title: "Delivery Returns", permissionKey: "deliveryReturns" },
      { title: "Purchase Returns", permissionKey: "purchaseReturns" },
      { title: "Trade Returns", permissionKey: "tradeReturns" },
    ],
  },
  {
    category: "Prices",
    permissions: [
      { title: "Prices Access", permissionKey: "prices" },
      {
        title: "Selling Price Histories",
        permissionKey: "sellingPriceHistories",
      },
      {
        title: "Purchase Price Histories",
        permissionKey: "purchasePriceHistories",
      },
    ],
  },
  {
    category: "Inventory",
    permissions: [
      { title: "Inventory Access", permissionKey: "inventory" },
      { title: "Inventory Dashboard", permissionKey: "inventoryDashboard" },
      { title: "Item Details", permissionKey: "itemDetails" },
      { title: "Physical Inventory", permissionKey: "physicalInventory" },
      { title: "Transfer Items", permissionKey: "transferItems" },
    ],
  },
  {
    category: "Staff Access",
    permissions: [
      { title: "Staff Access Control", permissionKey: "staffAccess" },
      { title: "Users", permissionKey: "users" },
      { title: "User Restrictions", permissionKey: "userRestriction" },
      { title: "Employees", permissionKey: "employees" },
      { title: "Approvers", permissionKey: "approvers" },
    ],
  },
  {
    category: "Back Tracking",
    permissions: [
      { title: "Back Tracking Access", permissionKey: "backTracking" },
      { title: "Stock Entry (Batches)", permissionKey: "batches" },
      { title: "Serial Numbers", permissionKey: "serialNumbers" },
      { title: "Audit Logs", permissionKey: "auditLogs" },
    ],
  },
  {
    category: "Settings",
    permissions: [
      { title: "Customer Credit", permissionKey: "customerCredit" },
      { title: "Pricing Display", permissionKey: "pricingDisplay" },
      { title: "Sales Order Settings", permissionKey: "salesOrderSettings" },
    ],
  },
  {
    category: "Others",
    permissions: [
      { title: "Locations", permissionKey: "locations" },
      { title: "Products", permissionKey: "productList" },
      { title: "Product BOM", permissionKey: "productBom" },
      { title: "Customers", permissionKey: "customers" },
      { title: "Vendors", permissionKey: "vendors" },
      { title: "Transactions", permissionKey: "transactions" },
      { title: "POS", permissionKey: "pos" },
    ],
  },
];

const flattenMenus = (structure) =>
  structure.flatMap((group) => group.permissions);

const rows = flattenMenus(MenuStructure);

// Get a list of permission keys that belong to the "Extra" sections
const extraCategories = ["Back Tracking", "Settings"];
const extraPermissionKeys = MenuStructure.filter((group) =>
  extraCategories.includes(group.category),
).flatMap((group) => group.permissions.map((p) => p.permissionKey));

const AddRestriction = ({ isOpen, setIsOpen, refreshData, jobRoleToEdit }) => {
  const currentUserID = useSelector(selectUserID);

  const [roleName, setRoleName] = useState("");
  const [permissions, setPermissions] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  // EXTRA FEATURES STATES
  const [isExtraUnlocked, setIsExtraUnlocked] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [extraPassword, setExtraPassword] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (jobRoleToEdit) {
        setRoleName(jobRoleToEdit.roleName || "");

        const updatedPerms = {};
        let shouldAutoUnlock = false;

        rows.forEach((item) => {
          const isGranted = jobRoleToEdit[item.permissionKey] || false;
          updatedPerms[item.permissionKey] = isGranted;

          // Auto-Unlock Logic: If they have ANY permission in the Extra categories checked
          if (isGranted && extraPermissionKeys.includes(item.permissionKey)) {
            shouldAutoUnlock = true;
          }
        });

        setPermissions(updatedPerms);
        setIsExtraUnlocked(shouldAutoUnlock);
        setShowPasswordPrompt(false);
        setExtraPassword("");
      } else {
        // Reset everything if creating a New Role
        setRoleName("");
        setPermissions({});
        setIsExtraUnlocked(false);
        setShowPasswordPrompt(false);
        setExtraPassword("");
      }
    } else {
      // Reset extra states when modal closes
      setIsExtraUnlocked(false);
      setShowPasswordPrompt(false);
      setExtraPassword("");
    }
  }, [jobRoleToEdit, isOpen]);

  // Handle Unlocking
  const handleUnlockExtra = () => {
    if (extraPassword === "1Chthus2018*") {
      setIsExtraUnlocked(true);
      setShowPasswordPrompt(false);
      setExtraPassword("");
      toast.success("Extra configurations unlocked!");
    } else {
      toast.error("Incorrect password!");
    }
  };

  const filteredMenu = useMemo(() => {
    // 1. Filter out locked categories if not unlocked
    let baseMenu = MenuStructure;
    if (!isExtraUnlocked) {
      baseMenu = baseMenu.filter(
        (group) => !extraCategories.includes(group.category),
      );
    }

    // 2. Filter by search query
    if (!searchQuery) return baseMenu;

    return baseMenu
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((p) =>
          p.title.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [searchQuery, isExtraUnlocked]);

  if (!isOpen) return null;

  const handleCheckboxChange = (permissionKey) => {
    setPermissions((prev) => ({
      ...prev,
      [permissionKey]: !prev[permissionKey],
    }));
  };

  const handleCategoryToggle = (category) => {
    const categoryPermissions =
      MenuStructure.find((item) => item.category === category)?.permissions ||
      [];
    const allChecked = categoryPermissions.every(
      (perm) => permissions[perm.permissionKey],
    );
    const updated = { ...permissions };
    categoryPermissions.forEach((perm) => {
      updated[perm.permissionKey] = !allChecked;
    });
    setPermissions(updated);
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      toast.warning("Please enter a role name");
      return;
    }

    const payload = {
      RoleName: roleName,
      UserID: currentUserID,
      ...rows.reduce((acc, item) => {
        const pascal =
          item.permissionKey.charAt(0).toUpperCase() +
          item.permissionKey.slice(1);
        acc[pascal] = !!permissions[item.permissionKey];
        return acc;
      }, {}),
    };

    const url = jobRoleToEdit
      ? `${domain}/api/JobRole/${jobRoleToEdit.id}`
      : `${domain}/api/JobRole`;

    try {
      if (jobRoleToEdit) {
        await axios.put(url, payload);
        toast.success("Permissions updated");
      } else {
        await axios.post(url, payload);
        toast.success("Role created");
      }
      setIsOpen(false);
      refreshData?.();
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 overflow-hidden">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl flex flex-col max-h-[92vh] border border-slate-200">
        <div className="flex items-center justify-between px-8 py-5 border-b border-slate-100 bg-slate-50/80 rounded-t-2xl">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-200">
              <FaShieldAlt size={20} />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">
                {jobRoleToEdit
                  ? "Modify Security Role"
                  : "New Access Control Role"}
              </h2>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">
                Permissions Management
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2.5 hover:bg-red-50 hover:text-red-500 rounded-full transition-all text-slate-400"
          >
            <FaTimes size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-end">
            <div className="lg:col-span-2">
              <label className="block text-sm font-bold text-slate-700 mb-2 ml-1">
                Role Designation
              </label>
              <input
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g. Senior Warehouse Supervisor"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all outline-none text-slate-700 text-lg font-medium"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 ml-1">
                Search Permissions
              </label>
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Filter by name..."
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none text-slate-700"
                />
              </div>
            </div>
          </div>

          <div className="h-px bg-slate-100" />

          <div>
            <div className="flex items-center justify-between mb-6 px-1">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                Module-Level Controls
                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-normal">
                  {Object.values(permissions).filter(Boolean).length} Active
                </span>
              </h3>

              {/* ===== EXTRA PASSWORD BUTTON LOGIC ===== */}
              <div>
                {!isExtraUnlocked ? (
                  showPasswordPrompt ? (
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm animate-fade-in">
                      <input
                        type="password"
                        placeholder="Enter password"
                        value={extraPassword}
                        onChange={(e) => setExtraPassword(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleUnlockExtra()
                        }
                        className="text-sm px-2 py-1 outline-none w-32 bg-transparent text-slate-700"
                        autoFocus
                      />
                      <div className="h-4 w-px bg-slate-300 mx-1"></div>
                      <button
                        onClick={handleUnlockExtra}
                        className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors uppercase px-1"
                      >
                        Unlock
                      </button>
                      <button
                        onClick={() => {
                          setShowPasswordPrompt(false);
                          setExtraPassword("");
                        }}
                        className="text-slate-400 hover:text-red-500 ml-1 transition-colors"
                      >
                        <FaTimes size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowPasswordPrompt(true)}
                      className="flex items-center gap-2 text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-100 px-4 py-2 rounded-xl shadow transition-all active:scale-95"
                    >
                      <FaLock size={10} className="text-slate-400" />
                      Extra
                    </button>
                  )
                ) : (
                  <div className="flex items-center gap-2 text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-xl">
                    <FaUnlock size={12} />
                    Extra Unlocked
                  </div>
                )}
              </div>
              {/* ======================================= */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredMenu.map((group, groupIdx) => {
                const categoryPerms = group.permissions;
                const activeCount = categoryPerms.filter(
                  (p) => permissions[p.permissionKey],
                ).length;
                const isAllSelected =
                  activeCount === categoryPerms.length &&
                  categoryPerms.length > 0;

                return (
                  <div
                    key={groupIdx}
                    className="flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-200"
                  >
                    <label className="flex items-center gap-3 p-4 bg-slate-50/50 border-b border-slate-100 rounded-t-2xl cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={() => handleCategoryToggle(group.category)}
                        className="w-5 h-5 rounded-md border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
                      />
                      <span className="font-bold text-slate-700 flex-grow truncate">
                        {group.category}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                          activeCount > 0
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-200 text-slate-500"
                        }`}
                      >
                        {activeCount}
                      </span>
                    </label>
                    <div className="p-3 space-y-1">
                      {categoryPerms.map((permission) => (
                        <label
                          key={permission.permissionKey}
                          className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={!!permissions[permission.permissionKey]}
                            onChange={() =>
                              handleCheckboxChange(permission.permissionKey)
                            }
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
                          />
                          <span className="text-sm text-slate-600 truncate">
                            {permission.title}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500 italic">
            Changes will be applied immediately to all users in this role.
          </p>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={() => setIsOpen(false)}
              className="flex-1 sm:flex-none px-8 py-3 text-slate-600 font-bold hover:bg-slate-200/50 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 sm:flex-none px-10 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              <FaCheck />
              {jobRoleToEdit ? "Save Updates" : "Confirm & Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRestriction;
